import { Link } from "react-router-dom";
import { footerColumns } from "@/mocks/navigation";
import { BRAND_NAME, BRAND_SHORT, BRAND_TAGLINE, LOGO_URL, SONGBO_URL, CATALOG_URL } from "@/mocks/site-config";
import { SALES_CHANNELS } from "@/mocks/channels";

export default function Footer() {
  return (
    <footer className="bg-foreground-900 text-background-100 relative z-[2]">
      <div className="mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* ── Top: Brand + Navigation ── */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          {/* Brand Column */}
          <div className="lg:w-[340px] flex-shrink-0">
            {/* Logo + Name */}
            <div className="flex items-center gap-3 mb-4">
              <img
                src={LOGO_URL}
                alt={BRAND_NAME}
                className="h-8 w-auto object-contain"
              />
              <span className="text-base font-heading tracking-[0.05em] text-background-100 whitespace-nowrap">
                {BRAND_SHORT}
              </span>
            </div>

            <p className="text-xs text-foreground-400 mb-1 tracking-wide">
              崧燁文化事業有限公司 · SUNYA
            </p>
            <p className="text-foreground-400 text-sm leading-relaxed mb-4 max-w-[280px]">
              {BRAND_TAGLINE}
            </p>

            {/* Key external portals */}
            <div className="flex flex-col gap-2 mb-4">
              <a
                href={SONGBO_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-400 hover:text-background-100 transition-colors cursor-pointer group"
              >
                <span>崧博出版主站</span>
                <i className="ri-external-link-line text-xs text-foreground-500 group-hover:text-background-100 transition-colors"></i>
              </a>
              <a
                href={CATALOG_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-400 hover:text-background-100 transition-colors cursor-pointer group"
              >
                <span>崧博書籍資料庫</span>
                <i className="ri-external-link-line text-xs text-foreground-500 group-hover:text-background-100 transition-colors"></i>
              </a>
            </div>

            {/* Brand relationship */}
            <p className="text-xs text-foreground-500 leading-relaxed mb-3 max-w-[280px]">
              本品牌委託崧博出版經銷，官方網站由崧博出版製作。
            </p>

            {/* Catalog data disclaimer */}
            <p className="text-xs text-foreground-500 leading-relaxed max-w-[280px]">
              完整書名、作者、ISBN、出版品牌及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
            </p>
          </div>

          {/* Navigation Columns */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-5">
            {footerColumns.map((col) => (
              <div key={col.title}>
                <h4 className="text-xs font-semibold text-foreground-500 mb-3.5 tracking-[0.08em] uppercase">
                  {col.title}
                </h4>
                <ul className="flex flex-col gap-2">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="text-sm text-foreground-400 hover:text-background-100 transition-colors cursor-pointer inline-flex items-center gap-1 group"
                        >
                          <span>{link.label}</span>
                          <i className="ri-external-link-line text-[11px] text-foreground-500 group-hover:text-background-100 transition-colors"></i>
                        </a>
                      ) : (
                        <Link
                          to={link.href}
                          className="text-sm text-foreground-400 hover:text-background-100 transition-colors cursor-pointer"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ── Sales Channels ── */}
        <div className="mt-10 pt-7 border-t border-foreground-800/60">
          <h4 className="text-xs font-semibold text-foreground-500 mb-3.5 tracking-[0.08em] uppercase">
            銷售通路
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-2.5">
            {SALES_CHANNELS.map((ch) => (
              <a
                key={ch.name}
                href={ch.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-sm text-foreground-400 hover:text-background-100 transition-colors cursor-pointer inline-flex items-center gap-1 group whitespace-nowrap"
              >
                <span>{ch.name}</span>
                <i className="ri-external-link-line text-[11px] text-foreground-500 group-hover:text-background-100 transition-colors"></i>
              </a>
            ))}
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="mt-8 pt-5 border-t border-foreground-800/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-xs text-foreground-500">
              &copy; {new Date().getFullYear()} 崧燁文化事業有限公司 SUNYA
            </p>
            <p className="text-xs text-foreground-600 leading-relaxed">
              本品牌委託崧博出版經銷，官方網站由崧博出版製作。
            </p>
          </div>
          <div className="flex items-center gap-5 flex-wrap">
            <span className="text-xs text-foreground-500">
              sunya.sonbook.net
            </span>
            <a
              href={SONGBO_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-xs text-foreground-500 hover:text-background-100 transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              sonbook.net <i className="ri-external-link-line text-[10px]"></i>
            </a>
            <a
              href={CATALOG_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-xs text-foreground-500 hover:text-background-100 transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              catalog.sonbook.net <i className="ri-external-link-line text-[10px]"></i>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}